import { useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button, DataTable, Pagination, LinkCell, Modal, TextInput, Select, useToast, useTableSort, type Column,
} from '@datahub/design-system'
import { get, post, downloadCsv } from '../../lib/api'
import { ListPage } from '../AdminShell'
import { StatusTag } from '../../components/StatusTag'
import { StearsCompanyPicker, type CoreCompanyPrefill } from './StearsCompanyPicker'
import { CompanyImportModal } from './CompanyImportModal'
import { ExternalLink } from '../../components/ExternalLink'
import { taxonomies } from '@datahub/shared'

interface CompanyRow {
  id: string
  displayName: string
  status: string
  sector: string | null
  countryOfIncorporation: string | null
  cadence: string | null
  owner: { displayName: string | null; email: string } | null
  contacts: { id: string; fullName: string; email: string; status: string }[]
  currentCycle: { periodLabel: string; status: string; deadlineAt: string | null; completionPct: number } | null
}

export function Companies() {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [form, setForm] = useState({ displayName: '', legalCompanyName: '', contactName: '', contactEmail: '' })
  /** What a Stears Core lookup filled in, carried through to the create call. */
  const [prefill, setPrefill] = useState<CoreCompanyPrefill | null>(null)
  const { sortKey, sortDir, onSort } = useTableSort('displayName')

  const q = params.get('q') ?? ''
  const status = params.get('status') ?? ''

  const { data, isLoading } = useQuery({
    queryKey: ['companies', q, status, page, pageSize, sortKey, sortDir],
    queryFn: () =>
      get<{ total: number; items: CompanyRow[] }>(
        `/admin/companies?${new URLSearchParams({
          ...(q ? { q } : {}), ...(status ? { status } : {}),
          page: String(page), pageSize: String(pageSize),
          ...(sortKey && sortDir ? { sort: sortKey, dir: sortDir } : {}),
        })}`,
      ),
  })

  const closeCreate = () => {
    setCreating(false)
    setForm({ displayName: '', legalCompanyName: '', contactName: '', contactEmail: '' })
    setPrefill(null)
  }

  const create = useMutation({
    mutationFn: () =>
      post<{ company: { id: string } }>('/admin/companies', {
        // The API re-reads the profile from Stears itself, so what lands on the record
        // is what Core actually says rather than whatever this page happens to hold.
        // The two name fields are sent as they appear, since the admin may have edited them.
        ...(prefill ? { coreSlug: prefill.slug } : {}),
        displayName: form.displayName.trim(),
        legalCompanyName: form.legalCompanyName.trim(),
        contact: { fullName: form.contactName.trim(), email: form.contactEmail.trim() },
      }),
    onSuccess: (res) => {
      toast.success('Company created as a draft. Set a cadence next.')
      closeCreate()
      void queryClient.invalidateQueries({ queryKey: ['companies'] })
      navigate(`/admin/companies/${res.company.id}`)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const columns: Column<CompanyRow>[] = [
    { key: 'displayName', header: 'Company', width: 260, sticky: true, sortable: true,
      render: (c) => <LinkCell onClick={() => navigate(`/admin/companies/${c.id}`)}>{c.displayName}</LinkCell> },
    { key: 'status', header: 'Status', width: 130, sortable: true, render: (c) => <StatusTag status={c.status} /> },
    { key: 'cadence', header: 'Cadence', width: 120, render: (c) => c.cadence ?? '-' },
    { key: 'cycle', header: 'Current cycle', width: 170,
      render: (c) => (c.currentCycle ? <StatusTag status={c.currentCycle.status} suffix={c.currentCycle.periodLabel} /> : '-') },
    { key: 'completion', header: 'Completion', width: 110, align: 'right',
      render: (c) => (c.currentCycle ? `${c.currentCycle.completionPct}%` : '-') },
    { key: 'contacts', header: 'Contacts', width: 260,
      render: (c) => (c.contacts.length === 0 ? '-' : c.contacts.map((k) => `${k.fullName} <${k.email}>`).join(', ')) },
    { key: 'sector', header: 'Sector', width: 170, render: (c) => c.sector ?? '-' },
    { key: 'country', header: 'Country', width: 140, render: (c) => c.countryOfIncorporation ?? '-' },
    { key: 'owner', header: 'Owner', width: 180, render: (c) => c.owner?.displayName ?? c.owner?.email ?? '-' },
  ]

  return (
    <ListPage
      title="Companies"
      actions={
        <>
          <Button variant="outline" size="medium" onClick={() => void downloadCsv('/admin/exports/companies', 'datahub-companies.csv')}>Export</Button>
          <Button variant="outline" size="medium" onClick={() => setImporting(true)}>Import</Button>
          <Button variant="filled-grey" size="medium" onClick={() => setCreating(true)}>New company</Button>
        </>
      }
    >
      <div className="flex items-center flex-shrink-0 bg-background-main" style={{ height: 54, padding: '0 20px', gap: 12, borderBottom: '0.5px solid #4B5563' }}>
        <TextInput size="small" value={q} onChange={(v) => { setParams(v ? { q: v } : {}); setPage(1) }} placeholder="Search companies or contact emails" className="w-[320px]" />
        <div style={{ width: 180 }}>
          <Select
            size="small"
            ariaLabel="Filter by company status"
            options={[{ value: '', label: 'All statuses' }, ...['draft', 'active', 'paused', 'archived'].map((s) => ({ value: s, label: s }))]}
            value={status}
            onChange={(v) => {
              const next = new URLSearchParams(params)
              if (v) next.set('status', v)
              else next.delete('status')
              setParams(next)
              setPage(1)
            }}
            placeholder="All statuses"
          />
        </div>
      </div>

      <div className="flex-1" style={{ overflow: 'hidden' }}>
        {isLoading ? (
          <p className="font-mulish text-text-support" style={{ fontSize: 12, padding: 20 }}>Loading…</p>
        ) : (
          <DataTable columns={columns} rows={data?.items ?? []} rowKey={(c) => c.id} sortKey={sortKey} sortDir={sortDir} onSort={onSort} caption="Companies" emptyMessage="No companies match those filters" maxHeight="100%" />
        )}
      </div>
      <Pagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1) }} />

      <CompanyImportModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={() => void queryClient.invalidateQueries({ queryKey: ['companies'] })}
      />

      <Modal
        open={creating}
        onClose={closeCreate}
        title="New company"
        primaryLabel="Create"
        primaryDisabled={!form.displayName.trim() || !form.legalCompanyName.trim() || !form.contactName.trim() || !form.contactEmail.trim()}
        primaryLoading={create.isPending}
        onPrimary={() => create.mutate()}
      >
        <div className="flex flex-col" style={{ gap: 20 }}>
          <StearsCompanyPicker
            disabled={create.isPending}
            onPick={(company) => {
              setPrefill(company)
              setForm((current) => ({
                ...current,
                displayName: company.displayName,
                legalCompanyName: company.legalCompanyName,
              }))
            }}
          />

          {prefill && <PrefillSummary prefill={prefill} onClear={() => setPrefill(null)} />}

          <TextInput label="Display name" size="large" value={form.displayName} onChange={(v) => setForm({ ...form, displayName: v })} />
          <TextInput label="Legal company name" size="large" value={form.legalCompanyName} onChange={(v) => setForm({ ...form, legalCompanyName: v })} />
          <TextInput label="Contact name" size="large" value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} />
          <TextInput label="Contact email" size="large" inputType="email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} />
          <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px' }}>
            The company starts as a draft. This contact becomes its primary contact; set a cadence before making it active.
          </p>
        </div>
      </Modal>
    </ListPage>
  )
}

/**
 * What the Stears lookup filled in beyond the two name fields. These are not editable
 * here — the modal is deliberately short — but the admin has to be able to see what is
 * about to be saved against the company, and to drop it if they picked the wrong match.
 */
function PrefillSummary({ prefill, onClear }: { prefill: CoreCompanyPrefill; onClear: () => void }) {
  const label = (slug: string | null, taxonomy: string) =>
    slug ? ((taxonomies[taxonomy] ?? []).find((t) => t.slug === slug)?.name ?? slug) : null

  const rows: [string, ReactNode][] = ([
    ['Sector', label(prefill.sector, 'sectors')],
    ['Sub-sectors', prefill.subSectors.map((s) => label(s, 'subsectors')).filter(Boolean).join(', ') || null],
    ['Country of incorporation', label(prefill.countryOfIncorporation, 'countries')],
    ['Countries of operation', prefill.countriesOfOperation.map((c) => label(c, 'countries')).filter(Boolean).join(', ') || null],
    ['Website', prefill.websiteUrl ? <ExternalLink value={prefill.websiteUrl} maxLength={34} /> : null],
    ['Founded', prefill.foundingYear ? String(prefill.foundingYear) : null],
    ['Ownership', prefill.ownershipStatus],
    ['Employees', prefill.employeeCount],
    ['Head office', prefill.hqAddress],
    ['Stears id', prefill.coreCompanyId],
  ] as [string, ReactNode][]).filter((r): r is [string, ReactNode] => Boolean(r[1]))

  return (
    <div style={{ border: '0.5px solid #4B5563', background: '#1A1B1E' }}>
      <div
        className="flex items-center justify-between"
        style={{ padding: '8px 12px', borderBottom: '0.5px solid #4B5563' }}
      >
        <span className="font-mulish text-white" style={{ fontSize: 12 }}>
          Pulled from Stears
        </span>
        <button
          type="button"
          onClick={onClear}
          className="text-text-hyperlink hover:underline font-mulish"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
        >
          Clear
        </button>
      </div>
      <div className="flex flex-col" style={{ padding: '8px 12px', gap: 4 }}>
        {rows.map(([key, value]) => (
          <div key={key} className="flex" style={{ gap: 8 }}>
            <span className="font-mulish text-text-support flex-shrink-0" style={{ fontSize: 10, width: '42%' }}>{key}</span>
            <span className="font-mulish text-white" style={{ fontSize: 10, flex: 1, wordBreak: 'break-word' }}>{value}</span>
          </div>
        ))}
        {prefill.description && (
          <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '15px', marginTop: 4 }}>
            {prefill.description.length > 260 ? `${prefill.description.slice(0, 260)}…` : prefill.description}
          </p>
        )}
      </div>
    </div>
  )
}
